import { useQuery } from '@tanstack/react-query';
import {
  Avatar,
  Badge,
  Card,
  EcoBarChart,
  EcoFigure,
  EcoHostingNote,
  EcoPanel,
  Eyebrow,
  Heading,
  LeafIcon,
} from '@openroad/ui';
import { api, unwrap } from '../lib/api';
import { useSession } from '../lib/session';

/**
 * "Your carbon, and Aotearoa's".
 *
 * Every number here comes from the API rather than being derived in the
 * browser: the personal total, the bars, the rank and the leaderboard all have
 * to agree with each other, and the only way to guarantee that is for one query
 * to define what a person-journey is. See `apps/server/src/modules/impact.ts`.
 */
const NUMBER = new Intl.NumberFormat('en-NZ');

export function EcoScreen() {
  const session = useSession();
  const signedIn = session.member !== null;

  const community = useQuery({
    queryKey: ['impact', 'community'],
    queryFn: () => unwrap(api.GET('/impact/community')),
  });

  const mine = useQuery({
    queryKey: ['impact', 'me'],
    queryFn: () => unwrap(api.GET('/impact/me')),
    enabled: signedIn,
  });

  const leaderboard = useQuery({
    queryKey: ['impact', 'leaderboard'],
    queryFn: () => unwrap(api.GET('/impact/leaderboard', { params: { query: { limit: 5 } } })),
  });

  return (
    <main className="mx-auto max-w-[1080px] px-6 pt-7 pb-16">
      <Eyebrow className="mb-1.5">Impact</Eyebrow>
      <Heading level="page" className="mb-6">
        Your carbon, and Aotearoa&rsquo;s
      </Heading>

      <div className="mb-[18px] grid gap-[18px] md:grid-cols-2">
        {/* personal */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <span className="text-label font-bold tracking-caps text-subtle uppercase">You</span>
            {mine.data?.rank ? (
              <Badge tone="ngahere">
                Rank #{mine.data.rank}
                {mine.data.region ? ` · ${mine.data.region}` : ''}
              </Badge>
            ) : null}
          </div>

          {!signedIn ? (
            <p className="mt-3.5 mb-0 text-base text-muted">
              Sign in to see what your shared rides have saved.
            </p>
          ) : mine.isPending ? (
            <p className="mt-3.5 mb-0 text-base text-muted">Loading your figures…</p>
          ) : mine.isError || !mine.data ? (
            <p className="mt-3.5 mb-0 text-base text-muted">
              We could not load your impact just now.
            </p>
          ) : (
            <>
              <div className="mt-3.5 mb-1 flex items-baseline gap-2">
                <span className="font-display text-stat-lg font-bold tracking-display tabular text-ngahere">
                  {NUMBER.format(mine.data.kilogramsSaved)}
                </span>
                <span className="text-lead text-muted">kg CO₂ saved</span>
              </div>
              <div className="mb-5 text-body-sm text-muted">
                across {mine.data.tripsShared}{' '}
                {mine.data.tripsShared === 1 ? 'shared trip' : 'shared trips'}
              </div>

              <EcoBarChart
                data={mine.data.monthly.map((bar) => ({
                  month: bar.label,
                  value: bar.kilograms,
                }))}
              />
            </>
          )}
        </Card>

        {/* community */}
        <EcoPanel className="relative overflow-hidden p-6">
          <span className="text-label font-bold tracking-caps text-ngahere-fore uppercase">
            Aotearoa community
          </span>

          {community.isPending ? (
            <p className="mt-3.5 mb-0 text-base text-ngahere-body">Loading…</p>
          ) : community.isError || !community.data ? (
            <p className="mt-3.5 mb-0 text-base text-ngahere-body">
              The community figures are not available right now.
            </p>
          ) : (
            <>
              <EcoFigure
                className="mt-3.5 mb-[18px]"
                value={NUMBER.format(community.data.totalKilogramsSaved)}
                unit="kg"
              />
              <div className="grid grid-cols-2 gap-3.5">
                {[
                  { value: community.data.tripsShared, label: 'trips shared' },
                  { value: community.data.activeMembers, label: 'active members' },
                  { value: community.data.treesEquivalent, label: 'trees / year equivalent' },
                  { value: community.data.kilometresShared, label: 'km shared' },
                ].map((stat) => (
                  <div key={stat.label}>
                    <div className="font-display text-heading font-bold tabular">
                      {NUMBER.format(stat.value)}
                    </div>
                    <div className="text-meta text-ngahere-body">{stat.label}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          <LeafIcon
            size={170}
            strokeWidth={1}
            aria-hidden="true"
            className="pointer-events-none absolute -right-6.5 -bottom-6.5 text-white opacity-[.16]"
          />
        </EcoPanel>
      </div>

      {/* leaderboard */}
      <Card className="p-[22px]">
        <Heading level="card" className="mb-4">
          Top eco travellers this month
        </Heading>

        {leaderboard.isPending ? (
          <p className="m-0 text-base text-muted">Loading…</p>
        ) : leaderboard.isError ? (
          <p className="m-0 text-base text-muted">The leaderboard is not available right now.</p>
        ) : leaderboard.data.items.length === 0 ? (
          <p className="m-0 text-base text-muted">
            Nobody has shared a ride this month yet. Be the first.
          </p>
        ) : (
          <ol className="m-0 flex list-none flex-col p-0">
            {leaderboard.data.items.map((entry) => (
              <li
                key={entry.userId}
                className="flex items-center gap-3.5 border-b border-line-soft py-3"
              >
                <span className="w-[22px] font-display text-base font-bold text-subtle">
                  {entry.rank}
                </span>
                <Avatar
                  initials={entry.initials}
                  colour={entry.avatarColour}
                  size={38}
                  aria-hidden="true"
                />
                <span className="flex-1 text-body-lg font-semibold">{entry.displayName}</span>
                <span className="flex items-center gap-1.5">
                  <LeafIcon size={15} strokeWidth={1.8} className="text-leaf" />
                  <span className="font-display text-body font-bold tabular text-ngahere">
                    {NUMBER.format(entry.kilogramsSaved)} kg
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}

        <EcoHostingNote className="mt-4" />
      </Card>
    </main>
  );
}
